<script lang="ts" setup>
import { watchDeep } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import AnimatedGradientText from '@/components/shared/base/AnimatedGradientText.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import ModalFill from '@/components/shared/composite/ModalFill.vue';
import { useSocketCall } from '@/composables/domains/chat/useSocketCall';
import { useMic } from '@/composables/shared/useMic';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { outerRadius } from '@/utils/deprecated/audioEffecter';

/**
 * @enum {VoiceStreamType}
 * @description 음성 스트림의 현재 상태를 나타냅니다.
 * @property PENDING - 음성 스트림 컴포넌트가 마운트될 때 설정되는 초기 상태입니다. (FE에서 설정)
 * @property INITIAL - 백엔드로부터 'VA' 메시지를 수신하여 스트림이 시작되었음을 나타내는 상태입니다. (FE에서 'VA' 메시지 수신 시, 설정)
 * @property VOICE_DETECTED - 음성이 감지되기 시작한 상태입니다.
 * @property COMPLETE - 음성 스트림이 성공적으로 완료된 상태입니다. (BE로부터 'VC'값 수신)
 * @property CANCEL - 음성 스트림이 의도적으로 취소된 상태입니다. (BE로부터 'VC'값 수신)
 * @property TIMED_OUT - 음성 스트림이 허용된 최대 시간(60초)을 초과하여 타임아웃된 상태입니다. (BE로부터 'VC'값 수신)
 * @property FAILED -  음성 스트림 처리 중 오류가 발생하여 실패한 상태입니다. (BE로부터 'VC'값 수신)
 * */

const props = withDefaults(defineProps<{ isDetail?: boolean }>(), {
  isDetail: false,
});

const { t } = useI18n();
const { micStop, micPrep, micStart, micClear } = useMic();
const audioStore = useAudioStore();
const userStore = useUserStore();
const { connectToWebsocket } = useSocketCall();
const { socketState } = useSocketStore();

// 'PENDING' 상태에서 일정 시간 내에 'INITIAL' 로 전환되지 않으면 'TIMED_OUT' 으로 처리하기 위한 타이머 ID
const socketTimeoutId = ref();
const restartTimeoutId = ref();

onMounted(async () => {
  await initStream();
  await startStream();
});
onUnmounted(() => {
  endStream();
});

const emit = defineEmits<{ close: []; start: [] }>();

// 'PENDING' 상태가 일정 시간(5초) 지속되면 'TIMED_OUT'으로 처리하는 함수
const checkSocketConnection = () => {
  socketTimeoutId.value = setTimeout(() => {
    if (audioStore.voiceStatus === 'PENDING') {
      endStream();
      audioStore.handleMessageVoiceStatus('TIMED_OUT');
    } else {
      clearTimeout(socketTimeoutId.value);
    }
  }, 5000);
};

// 음성 스트림 초기화 함수
const initStream = async () => {
  audioStore.handleMessageVoiceStatus('PENDING');
  await audioStore.initDevices();
  await connectToWebsocket(true, { connectionType: 'VOICE-STREAMING' });
  checkSocketConnection();
};

// 음성 스트림 시작 함수
const startStream = async () => {
  await micPrep('voice_streaming', audioStore.selectedInput);
  await micStart('voice_streaming', audioStore.selectedInput, {
    userId: userStore.userId,
    deviceLabel: audioStore.selectedInput?.label || 'Default',
    languageCode: audioStore.languageSelected?.code || 'ko',
    reference: props.isDetail
      ? t('voice-streaming-detail-reading-text')
      : t('voice-streaming-reading-text'),
    isPartnerPerson: '1',
  });
};

// 음성 스트림 종료 함수
const endStream = async (restart?: boolean) => {
  clearInterval(socketTimeoutId.value);
  micStop('voice_streaming', { userId: userStore.userId });
  await micClear('voice_streaming');

  if (restart) {
    restartTimeoutId.value = setTimeout(async () => {
      clearTimeout(restartTimeoutId.value);
      await initStream();
      await startStream();
    }, 3000);
  }
};

// 모달 제목
const modalTitle = computed<string>(() => {
  return (
    {
      PENDING: '',
      INITIAL: props.isDetail
        ? t('voice-streaming-initial-detail-title')
        : t('voice-streaming-initial-title'),
      VOICE_DETECTED: props.isDetail
        ? t('voice-streaming-initial-detail-title')
        : t('voice-streaming-initial-title'),
      COMPLETE: props.isDetail
        ? t('voice-streaming-detail-title')
        : t('voice-streaming-title'),
      CANCEL: t('voice-streaming-timeout'),
      TIMED_OUT: t('voice-streaming-timeout'),
      FAILED: t('voice-streaming-timeout'),
    }[audioStore.voiceStatus] || ''
  );
});

// 모달 부제목
const modalSubtitle = computed<string>(() => {
  return (
    {
      PENDING: '',
      INITIAL: t('voice-streaming-initial-subtitle'),
      VOICE_DETECTED: t('voice-streaming-initial-subtitle'),
      COMPLETE: t('voice-streaming-detail-complete-subtitle'),
      CANCEL: '',
      TIMED_OUT: '',
      FAILED: '',
    }[audioStore.voiceStatus] || ''
  );
});

// 모달 음성등록 텍스트
const modalReadingText = computed<string>(() => {
  return (
    {
      PENDING: '',
      INITIAL: props.isDetail
        ? t('voice-streaming-detail-reading-text')
        : t('voice-streaming-reading-text'),
      VOICE_DETECTED: props.isDetail
        ? t('voice-streaming-detail-reading-text')
        : t('voice-streaming-reading-text'),
      COMPLETE: '',
      CANCEL: '',
      TIMED_OUT: '',
      FAILED: '',
    }[audioStore.voiceStatus] || ''
  );
});

const videoSource = computed<string>(() => {
  return (
    {
      PENDING: '/images/scon-linear.mp4',
      INITIAL: '/images/scon-linear.mp4',
      VOICE_DETECTED: '/images/scon-linear.mp4',
      COMPLETE: '/images/scon-linear.mp4',
      CANCEL: '/images/scon-linear.mp4',
      TIMED_OUT: '/images/scon-linear.mp4',
      FAILED: '/images/scon-linear.mp4',
    }[audioStore.voiceStatus] || ''
  );
});

//목소리 분석 애니메이션
const analyzingIndex = ref(0);
let analyzingIntervalId: ReturnType<typeof setInterval>;

onMounted(() => {
  analyzingIntervalId = setInterval(() => {
    analyzingIndex.value = (analyzingIndex.value + 1) % 2;
  }, 2000);
});

onUnmounted(() => {
  clearInterval(analyzingIntervalId);
});

// 음성 데시벨에 따라, 'VOICE_DETECTED' 혹은 'INITIAL' 로 처리합니다.
watchDeep(
  () => outerRadius.value,
  (val: number) => {
    const changedStatus = val >= 1.0 ? 'VOICE_DETECTED' : 'INITIAL';

    if (
      ['INITIAL', 'VOICE_DETECTED'].includes(audioStore.voiceStatus) &&
      changedStatus !== audioStore.voiceStatus
    ) {
      audioStore.handleMessageVoiceStatus(changedStatus);
    }
  }
);

/**
 * @enum {SocketState} - 소켓상태
 * @description 소켓 상태가 정상적인 연결을 지속할 수 없을 경우, 'FAILED' 로 처리합니다.
 * */
watch(
  () => socketState,
  (state) => {
    if (['ERROR_NO_HEADER', 'CLOSED', 'RETRY_FAILED'].includes(state)) {
      audioStore.handleMessageVoiceStatus('FAILED');
    }
  }
);

/**
 * @enum {VoiceStreamType} - 음성스트림 상태
 * @description 음성스트림 상태에 따른 함수실행
 * 1. 'COMPLETE' → 종료
 * 2. 'TIMED_OUT', 'FAILED' → 종료 및 3초후 재실행
 * */
watch(
  () => audioStore.voiceStatus,
  async (val) => {
    if (['COMPLETE', 'TIMED_OUT', 'FAILED'].includes(val)) {
      const restart: boolean = ['TIMED_OUT', 'FAILED'].includes(val);
      await endStream(restart);
    }
  }
);
</script>

<template>
  <ModalFill>
    <div class="container-addvoice">
      <div class="wrapper-addvoice">
        <div class="wrapper-video" :data-state="audioStore.voiceStatus">
          <video autoplay loop muted class="status-video">
            <source :src="videoSource" type="video/mp4" />
          </video>
          <div class="side left" />
          <div class="side right" />
        </div>

        <!--내용-->
        <div class="wrapper-content fadeinOnboarding">
          <!--첫문장-->
          <h3 v-if="modalTitle" v-html="modalTitle" class="regular" />
          <!--두번째문장-->
          <AnimatedGradientText>
            <h3>{{ modalReadingText }}</h3>
          </AnimatedGradientText>
          <!--세번째문장-->
          <h3 v-if="modalSubtitle" class="regular" v-text="modalSubtitle" />
        </div>

        <!--분석중
        <div class="wrapper-analyzing">
          <IconSoundLoading class="icon-loading" />
          <p class="dim">
            {{
              analyzingIndex === 0
                ? '목소리를 분석 중입니다.'
                : '잠시만 기다려주세요.'
            }}
          </p>
        </div>
        -->

        <div class="wrapper-action">
          <ButtonProcess
            v-if="audioStore.voiceStatus === 'COMPLETE'"
            full-width
            @click="emit('start')"
          >
            {{ t('button_start') }}
          </ButtonProcess>
          <a v-else type="button" @click.prevent="emit('close')" class="dim">
            {{ t('do-it-later') }}
          </a>
        </div>
      </div>
    </div>
  </ModalFill>
</template>

<style scoped lang="scss">
@import '@/assets/sass/theme/_light.scss';

.container-addvoice {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 100dvh;
  word-break: keep-all;
  position: relative;
}

.wrapper-addvoice {
  display: flex;
  flex-direction: column;
  width: 25rem;
  height: 70dvh;
  min-height: 25rem;
  align-items: center;
  text-align: center;

  @include respond-to(mobile-landscape) {
    width: 100%;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
}

.wrapper-video {
  position: relative;
  display: flex;
  width: 25rem;
  height: 10rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  overflow: hidden;

  &::before,
  &::after,
  .side {
    content: '';
    position: absolute;
    pointer-events: none;
    z-index: 2;
  }

  /* 위 */
  &::before {
    top: 0;
    left: 0;
    width: 100%;
    height: 5rem;
    background: linear-gradient(to bottom, #fff, transparent);
  }

  /* 아래 */
  &::after {
    bottom: 0;
    left: 0;
    width: 100%;
    height: 5rem;
    background: linear-gradient(to top, #fff, transparent);
  }

  /* 좌 */
  .side.left {
    top: 0;
    left: 0;
    width: 3rem;
    height: 100%;
    background: linear-gradient(to right, #fff, transparent);
  }

  /* 우 */
  .side.right {
    top: 0;
    right: 0;
    width: 3rem;
    height: 100%;
    background: linear-gradient(to left, #fff, transparent);
  }

  .status-video {
    display: block;
    flex: 0 0 auto;
    width: 100%;
    min-width: 100%;
    height: auto;
    transition:
      transform 240ms ease,
      filter 240ms ease,
      opacity 240ms ease,
      outline-color 240ms ease;
  }

  &[data-state='INITIAL'] .status-video {
    filter: none;
    opacity: 1;
  }

  &[data-state='VOICE_DETECTED'] .status-video {
    transform: scale(1.5);
    opacity: 1;
  }

  &[data-state='COMPLETE'] .status-video {
    filter: grayscale(20%) brightness(105%);
    transform: scale(0.98);
    opacity: 1;
  }

  &[data-state='PENDING'],
  &[data-state='CANCEL'],
  &[data-state='TIMED_OUT'],
  &[data-state='FAILED'] {
    .status-video {
      filter: grayscale(100%) blur(0.5px);
      opacity: 0.7;
    }
  }
}

.wrapper-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wrapper-analyzing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.icon-loading {
  width: 2rem;
  height: 2rem;
}

.fadeinOnboarding {
  opacity: 0;
  animation: fadeInOnboarding 0.8s ease forwards;
  animation-delay: 0.5s;
}

.wrapper-action {
  display: flex;
  width: 100%;
  margin-top: auto;
  padding-bottom: 2rem;
  justify-content: center;
}

@keyframes fadeInOnboarding {
  to {
    opacity: 1;
  }
}

.btn {
  max-width: 15rem;
}
</style>
